import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { ClipboardList, Plus, Edit2, Trash2, X, Save, AlertCircle, Upload, FileText } from 'lucide-react';
import { clientIntakeTemplate, forms as formsApi } from '../utils/api';

/**
 * ClientIntakeChecklistEditor
 * ────────────────────────────
 * Admin card for managing the "intake checklist" that gets cloned into
 * pending document requests every time a new client self-signs up
 * (roadmap item #14 Phase 2).
 *
 *   - List, add, edit, delete template rows (title / instructions /
 *     type / required flag).
 *   - Toggle `block_signup_until_required_documents` — when on,
 *     self-signed-up clients with required pending intake items are
 *     routed to a gated page until they fulfill every required one.
 *
 * Admin-created clients do NOT trigger auto-spawn — this checklist is
 * only for self-signup.
 */

const ALL_TYPES = ['vaccine_record', 'waiver', 'contract', 'form_of_id', 'health_records', 'other'];
const TYPE_LABELS = {
  vaccine_record: 'Vaccine record',
  waiver: 'Waiver',
  contract: 'Contract',
  form_of_id: 'Form of ID',
  health_records: 'Health records',
  other: 'Other',
};

const emptyForm = { title: '', instructions: '', documentType: 'other', required: false, kind: 'document', formId: '' };

const ClientIntakeChecklistEditor = () => {
  const [items, setItems] = useState([]);
  const [gatingEnabled, setGatingEnabled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [savingGate, setSavingGate] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  // Active-forms list for the kind='form' picker. Loaded on first modal
  // open and cached for the editor's lifetime.
  const [availableForms, setAvailableForms] = useState(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [itemsRes, settingsRes] = await Promise.all([
        clientIntakeTemplate.list(),
        // Use list endpoint to also surface settings. Settings is a
        // separate endpoint — fetch it directly.
        fetch('/api/client-intake-template/settings', {
          headers: { Authorization: `Bearer ${sessionStorage.getItem('token')}` },
        }).then((r) => r.json()).catch(() => ({})),
      ]);
      setItems(itemsRes.data || []);
      setGatingEnabled(!!settingsRes.block_signup_until_required_documents);
    } catch (err) {
      console.error('Load intake checklist error:', err);
      toast.error('Failed to load intake checklist');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Gating toggle ──────────────────────────────────────────────────

  const handleToggleGating = async (next) => {
    setSavingGate(true);
    const previous = gatingEnabled;
    setGatingEnabled(next); // optimistic
    try {
      await fetch('/api/client-intake-template/settings', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${sessionStorage.getItem('token')}`,
        },
        body: JSON.stringify({ block_signup_until_required_documents: next }),
      }).then(async (r) => {
        if (!r.ok) throw new Error((await r.json()).error || 'Failed');
      });
      toast.success(next ? 'Signup will now require required documents' : 'Signup gating disabled');
    } catch (err) {
      setGatingEnabled(previous);
      toast.error(err.message || 'Failed to update gating');
    } finally {
      setSavingGate(false);
    }
  };

  // ── Item CRUD ──────────────────────────────────────────────────────

  const ensureFormsLoaded = useCallback(async () => {
    if (availableForms !== null) return;
    try {
      const res = await formsApi.list();
      setAvailableForms((res.data || []).filter((f) => !!f.is_active));
    } catch (err) {
      setAvailableForms([]);
    }
  }, [availableForms]);

  const openAdd = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setShowModal(true);
    ensureFormsLoaded();
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm({
      title: item.title || '',
      instructions: item.instructions || '',
      documentType: item.document_type || 'other',
      required: !!item.required,
      kind: item.kind || 'document',
      formId: item.form_id || '',
    });
    setShowModal(true);
    ensureFormsLoaded();
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm({ ...emptyForm });
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (form.kind === 'form' && !form.formId) {
      toast.error('Please pick a form');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        instructions: form.instructions.trim() || null,
        documentType: form.documentType,
        required: form.required,
        kind: form.kind,
        form_id: form.kind === 'form' ? form.formId : null,
        // Append at the end when creating; preserve order on edit.
        sort_order: editingId
          ? (items.find((i) => i.id === editingId)?.sort_order ?? 0)
          : items.length,
      };
      if (editingId) {
        await clientIntakeTemplate.update(editingId, payload);
        toast.success('Intake item updated');
      } else {
        await clientIntakeTemplate.create(payload);
        toast.success('Intake item added');
      }
      closeModal();
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save intake item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete "${item.title}" from the intake checklist?\n\nExisting clients who were already asked for this won't be affected.`)) return;
    try {
      await clientIntakeTemplate.delete(item.id);
      toast.success('Intake item removed');
      await loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  if (loading) {
    return (
      <div className="card">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      </div>
    );
  }

  const hasRequired = items.some((i) => i.required);

  return (
    <div className="card">
      <div className="flex items-center space-x-3 mb-6">
        <ClipboardList className="text-primary-600" size={24} />
        <h2 className="text-2xl font-bold">Client Intake Checklist</h2>
      </div>

      <p className="text-gray-600 mb-6">
        Items in this checklist are automatically requested from every new client when they create their
        own account (self-signup). Admin-created accounts skip this — admins are assumed to already have
        what they need. Each item becomes a pending document request on the new client's profile.
      </p>

      {/* Gating toggle */}
      <div className="bg-gray-50 p-4 rounded-lg mb-6">
        <div className="flex items-start space-x-3">
          <input
            type="checkbox"
            id="intake_gate"
            checked={gatingEnabled}
            onChange={(e) => handleToggleGating(e.target.checked)}
            disabled={savingGate}
            className="mt-1 h-5 w-5 text-primary-600 focus:ring-primary-500 border-gray-300 rounded disabled:opacity-50"
          />
          <div className="flex-1">
            <label htmlFor="intake_gate" className="font-semibold text-gray-900 cursor-pointer block">
              Block signup until required documents are uploaded
            </label>
            <p className="text-sm text-gray-600 mt-1">
              When enabled, a self-signed-up client cannot use the app until they've uploaded every
              <strong> required</strong> intake item below. They'll be routed to a gated upload page right
              after signup. Optional items don't block — they just appear on the new client's profile as
              pending requests.
            </p>
            {gatingEnabled && !hasRequired && (
              <p className="text-xs text-amber-700 mt-2 flex items-center gap-1">
                <AlertCircle size={14} />
                Gating is on but no items are marked required — clients won't actually be blocked. Mark at
                least one item as required below.
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Items list */}
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-gray-900">Checklist items</h3>
        <button onClick={openAdd} className="btn btn-primary flex items-center gap-2">
          <Plus size={16} />
          <span>Add item</span>
        </button>
      </div>

      {items.length === 0 ? (
        <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
          <ClipboardList className="mx-auto mb-2 text-gray-400" size={32} />
          <p className="text-sm">No checklist items yet. New self-signups won't be asked for anything.</p>
          <button onClick={openAdd} className="btn btn-secondary mt-3">Add your first item</button>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          {items.map((item) => (
            <li key={item.id} className="p-3 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-medium text-gray-900">{item.title}</span>
                  {item.kind === 'form' ? (
                    <span className="text-xs px-2 py-0.5 bg-indigo-100 text-indigo-800 rounded inline-flex items-center gap-1">
                      <ClipboardList size={12} /> Form{item.form_name ? `: ${item.form_name}` : ''}
                      {item.form_id && !item.form_is_active && (
                        <span className="ml-1 text-red-700">(inactive!)</span>
                      )}
                    </span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-700 rounded">
                      {TYPE_LABELS[item.document_type] || item.document_type}
                    </span>
                  )}
                  {item.required ? (
                    <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">Required</span>
                  ) : (
                    <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">Optional</span>
                  )}
                </div>
                {item.instructions && (
                  <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{item.instructions}</p>
                )}
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => openEdit(item)}
                  className="p-2 text-gray-600 hover:text-primary-600"
                  title="Edit"
                >
                  <Edit2 size={16} />
                </button>
                <button
                  onClick={() => handleDelete(item)}
                  className="p-2 text-red-600 hover:text-red-800"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="text-xs text-gray-500 mt-4">
        Edits and deletions here only affect <strong>future</strong> signups. Existing clients keep whatever
        was asked of them at signup time — you can manage their individual requests from Client Management.
      </p>

      {/* ── Add / Edit Modal ──────────────────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold">
                {editingId ? 'Edit intake item' : 'Add intake item'}
              </h3>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">What does the new client need to do?</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, kind: 'document' })}
                    className={`p-3 rounded-lg border text-left transition ${form.kind === 'document' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-2 font-medium text-gray-900">
                      <Upload size={16} /> Upload a file
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Image or PDF (e.g. vaccine cert, ID).</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => { setForm({ ...form, kind: 'form' }); ensureFormsLoaded(); }}
                    className={`p-3 rounded-lg border text-left transition ${form.kind === 'form' ? 'border-primary-500 bg-primary-50' : 'border-gray-200 hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-2 font-medium text-gray-900">
                      <FileText size={16} /> Fill out a form
                    </div>
                    <p className="text-xs text-gray-600 mt-1">A structured form you've built.</p>
                  </button>
                </div>
              </div>

              {form.kind === 'form' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Form <span className="text-red-500">*</span>
                  </label>
                  {availableForms === null ? (
                    <div className="text-sm text-gray-500">Loading forms…</div>
                  ) : availableForms.length === 0 ? (
                    <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                      No active forms yet. Build one under <strong>Forms</strong>, mark it Active, then come back.
                    </div>
                  ) : (
                    <select
                      value={form.formId}
                      onChange={(e) => setForm({ ...form, formId: e.target.value })}
                      className="input w-full"
                    >
                      <option value="">— pick a form —</option>
                      {availableForms.map((f) => (
                        <option key={f.id} value={f.id}>{f.name}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="input w-full"
                  placeholder={form.kind === 'form' ? 'e.g. "New client intake form"' : 'e.g. "Rabies certificate"'}
                  maxLength={200}
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Instructions <span className="text-gray-400 text-xs">(optional)</span>
                </label>
                <textarea
                  value={form.instructions}
                  onChange={(e) => setForm({ ...form, instructions: e.target.value })}
                  className="input w-full"
                  rows={3}
                  placeholder={form.kind === 'form'
                    ? "Anything the client should know before opening the form."
                    : "Tell new clients exactly what to upload (e.g. 'Must be current within the last 12 months. PDF preferred.')"}
                />
              </div>
              {form.kind === 'document' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Document type</label>
                  <select
                    value={form.documentType}
                    onChange={(e) => setForm({ ...form, documentType: e.target.value })}
                    className="input w-full"
                  >
                    {ALL_TYPES.map((t) => (
                      <option key={t} value={t}>{TYPE_LABELS[t]}</option>
                    ))}
                  </select>
                </div>
              )}
              <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.required}
                  onChange={(e) => setForm({ ...form, required: e.target.checked })}
                  className="rounded"
                />
                <span>Mark as required</span>
                <span className="text-xs text-gray-500">(blocks signup when gating is on)</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button onClick={closeModal} className="btn btn-secondary" disabled={saving}>Cancel</button>
              <button onClick={handleSave} className="btn btn-primary flex items-center gap-2" disabled={saving}>
                <Save size={16} />
                <span>{saving ? 'Saving…' : (editingId ? 'Save changes' : 'Add item')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientIntakeChecklistEditor;
